import {
  GroupChat,
  GroupChatMemberProfile,
  GroupChatMessage,
  GroupChatMessageReaction,
  GroupChatWithPreview,
  supabase,
  TABLES,
} from '@/lib/supabase';

/** PostgREST `.in()` blows up on long URL query strings — chunk large ID lists. */
const IN_CHUNK_SIZE = 80;

async function selectInChunks<T>(
  table: string,
  idColumn: string,
  ids: string[],
  select: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applyQuery?: (query: any) => any
): Promise<{ data: T[]; error: unknown }> {
  if (ids.length === 0) return { data: [], error: null };

  const all: T[] = [];
  for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + IN_CHUNK_SIZE);
    const base = supabase.from(table).select(select).in(idColumn, chunk);
    const query = applyQuery ? applyQuery(base) : base;
    const { data, error } = await query;
    if (error) return { data: all, error };
    if (data) all.push(...(data as T[]));
  }
  return { data: all, error: null };
}

export async function isGamerplugTeamMember(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from(TABLES.GAMERPLUG_TEAM_MEMBERS)
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('isGamerplugTeamMember error:', error);
    return false;
  }

  return !!data;
}

export async function getUserGroupChats(userId: string): Promise<GroupChatWithPreview[]> {
  // Embed group_chats(*) directly through the membership join so we never have to
  // round-trip a (potentially huge) list of chat ids back through `.in('id', …)`,
  // which 414s on accounts that belong to hundreds of chats.
  const { data: memberRows, error: memberError } = await supabase
    .from(TABLES.GROUP_CHAT_MEMBERS)
    .select('group_chat_id, last_read_at, joined_at, group_chats(*)')
    .eq('user_id', userId);

  if (memberError) {
    console.error('getUserGroupChats members error:', memberError);
    return [];
  }
  if (!memberRows?.length) return [];

  const chatIds = memberRows.map((row) => row.group_chat_id as string);

  const chats = memberRows
    .map((row) => {
      const embedded = (row as { group_chats?: GroupChat | GroupChat[] | null }).group_chats;
      return Array.isArray(embedded) ? embedded[0] ?? null : embedded ?? null;
    })
    .filter((chat): chat is GroupChat => chat != null)
    // Tournament chats power the per-event activity feed, not the Messages list.
    .filter((chat) => chat.tournament_id == null)
    .sort((a, b) => {
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });

  const { data: allMembers, error: allMembersError } = await selectInChunks<{
    group_chat_id: string;
    user_id: string;
    users: { gamertag: string; profile_image_url?: string } | { gamertag: string; profile_image_url?: string }[];
  }>(
    TABLES.GROUP_CHAT_MEMBERS,
    'group_chat_id',
    chatIds,
    'group_chat_id, user_id, users:users!inner(gamertag, profile_image_url)'
  );

  if (allMembersError) {
    console.error('getUserGroupChats all-members error:', allMembersError);
  }

  const { data: lastMessages } = await selectInChunks<GroupChatMessage>(
    TABLES.GROUP_CHAT_MESSAGES,
    'group_chat_id',
    chatIds,
    'group_chat_id, content, created_at, sender_id',
    // Soft-deleted messages must not surface as a conversation preview.
    (query) => query.is('deleted_at', null).order('created_at', { ascending: false })
  );

  const { data: teamRows } = await supabase
    .from(TABLES.GAMERPLUG_TEAM_MEMBERS)
    .select('user_id');
  const teamIds = new Set<string>((teamRows ?? []).map((row) => row.user_id as string));
  const viewerIsTeam = teamIds.has(userId);

  const nonTeamMembersByChat = new Map<string, Set<string>>();
  (allMembers ?? []).forEach((row) => {
    const memberId = row.user_id as string;
    if (teamIds.has(memberId)) return;
    const chatId = row.group_chat_id as string;
    if (!nonTeamMembersByChat.has(chatId)) nonTeamMembersByChat.set(chatId, new Set());
    nonTeamMembersByChat.get(chatId)!.add(memberId);
  });

  const chatsWithNonTeamMessage = new Set<string>();
  (lastMessages ?? []).forEach((message) => {
    if (!teamIds.has(message.sender_id as string)) {
      chatsWithNonTeamMessage.add(message.group_chat_id as string);
    }
  });

  const lastByChat = new Map<string, GroupChatMessage>();
  (lastMessages ?? []).forEach((message) => {
    const chatId = message.group_chat_id as string;
    if (!lastByChat.has(chatId)) lastByChat.set(chatId, message as GroupChatMessage);
  });

  const cutoffByChat = new Map<string, string>();
  memberRows.forEach((row) => {
    cutoffByChat.set(row.group_chat_id as string, (row.last_read_at as string) ?? (row.joined_at as string));
  });

  const isWelcomeChat = (title: string | null | undefined) =>
    !!title && title.startsWith('Gamerplug x ');

  const visibleChats = viewerIsTeam
    ? (chats as GroupChat[]).filter((chat) => {
        // A squad chat is one the viewer deliberately formed by accepting
        // players, so it is never noise. Without this exemption it was hidden
        // from its own host: the only message is the roster card, which is
        // attributed to the host, so a host on the team saw a chat open and
        // then vanish from their Messages list until somebody else typed.
        if (chat.kind === 'squad') return true;
        if (isWelcomeChat(chat.title)) {
          return chatsWithNonTeamMessage.has(chat.id);
        }
        const hasNonTeamMember = (nonTeamMembersByChat.get(chat.id)?.size ?? 0) > 0;
        return !hasNonTeamMember || chatsWithNonTeamMessage.has(chat.id);
      })
    : (chats as GroupChat[]);

  return visibleChats.map((chat) => {
    const members: GroupChatMemberProfile[] = (allMembers ?? [])
      .filter((row) => row.group_chat_id === chat.id)
      .map((row) => {
        const profile = Array.isArray(row.users) ? row.users[0] : row.users;
        return {
          user_id: row.user_id as string,
          gamertag: profile?.gamertag ?? 'user',
          profile_image_url: profile?.profile_image_url ?? undefined,
        };
      });

    const lastMessage = lastByChat.get(chat.id);
    const cutoff = cutoffByChat.get(chat.id);
    const hasUnread =
      !!lastMessage &&
      !!cutoff &&
      lastMessage.created_at > cutoff &&
      lastMessage.sender_id !== userId;

    return {
      ...chat,
      members,
      lastMessage: lastMessage
        ? {
            content: lastMessage.content,
            created_at: lastMessage.created_at,
            sender_id: lastMessage.sender_id,
          }
        : undefined,
      hasUnread,
    };
  });
}

/**
 * The user's "Gamerplug x <gamertag>" chat with the team, created by the
 * create_welcome_group_chat trigger when a gamertag is set. Accounts that
 * predate the trigger (Apr 2026) have none, so callers need a fallback. A
 * renamed gamertag gets a fresh chat, so take the newest.
 */
export async function getWelcomeGroupChatId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('group_chat_members')
    .select('group_chat_id, group_chats!inner(title, created_at)')
    .eq('user_id', userId)
    .like('group_chats.title', 'Gamerplug x %')
    .is('group_chats.tournament_id', null);
  const rows = (data ?? []) as unknown as {
    group_chat_id: string;
    group_chats: { created_at: string };
  }[];
  rows.sort((a, b) => b.group_chats.created_at.localeCompare(a.group_chats.created_at));
  return rows[0]?.group_chat_id ?? null;
}

export async function getGroupChatById(groupChatId: string): Promise<GroupChat | null> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHATS)
    .select('*')
    .eq('id', groupChatId)
    .maybeSingle();

  if (error) {
    console.error('getGroupChatById error:', error);
    return null;
  }
  return data as GroupChat | null;
}

export async function getGroupChatMessages(
  groupChatId: string,
  limit = 100
): Promise<GroupChatMessage[]> {
  // Fetch the newest `limit` messages (descending), then reverse to
  // oldest→newest for rendering. Ordering ascending before limiting would
  // return the OLDEST 100 messages and hide all recent ones once a chat
  // exceeds the limit.
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .select('*')
    .eq('group_chat_id', groupChatId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('getGroupChatMessages error:', error);
    return [];
  }
  return ((data ?? []) as GroupChatMessage[]).reverse();
}

/**
 * Resolve gamertags/avatars for arbitrary user ids. Used to render system
 * notices ("Alex left the group") whose subject is no longer a member.
 */
export async function getUserProfilesByIds(
  ids: string[]
): Promise<GroupChatMemberProfile[]> {
  if (!ids.length) return [];
  const { data, error } = await selectInChunks<{
    id: string;
    gamertag: string;
    profile_image_url?: string;
  }>(TABLES.USERS, 'id', ids, 'id, gamertag, profile_image_url');

  if (error) {
    console.error('getUserProfilesByIds error:', error);
    return [];
  }
  return (data ?? []).map((row) => ({
    user_id: row.id,
    gamertag: row.gamertag ?? 'user',
    profile_image_url: row.profile_image_url ?? undefined,
  }));
}

export async function getGroupChatMembers(
  groupChatId: string
): Promise<GroupChatMemberProfile[]> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MEMBERS)
    .select('user_id, last_read_at, users:users!inner(gamertag, profile_image_url)')
    .eq('group_chat_id', groupChatId);

  if (error || !data) {
    console.error('getGroupChatMembers error:', error);
    return [];
  }

  return data.map((row) => {
    const profile = Array.isArray(row.users) ? row.users[0] : row.users;
    return {
      user_id: row.user_id as string,
      gamertag: profile?.gamertag ?? 'user',
      profile_image_url: profile?.profile_image_url ?? undefined,
      last_read_at: (row.last_read_at as string | null) ?? null,
    };
  });
}

/**
 * Turn a message-insert failure into something worth showing a player.
 *
 * The "members send messages" policy covers three cases at once — not a member,
 * a forged sender_id, and a tournament ban — and Postgres reports all of them as
 * the same 42501 row-level-security error. Leaking that string put
 * `new row violates row-level security policy for table "group_chat_messages"`
 * under the composer, which tells a banned player nothing about why they can no
 * longer post.
 */
function describeSendError(error: { code?: string; message: string }): string {
  const isPolicyViolation =
    error.code === '42501' ||
    error.message.includes('row-level security policy');

  return isPolicyViolation
    ? "You can't post in this chat."
    : 'Failed to send message. Please try again.';
}

export async function sendGroupMessage(params: {
  groupChatId: string;
  senderId: string;
  content: string;
  mentions?: string[];
}): Promise<{ success: boolean; message?: GroupChatMessage; error?: string }> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .insert({
      group_chat_id: params.groupChatId,
      sender_id: params.senderId,
      content: params.content,
      mentions: params.mentions?.length ? params.mentions : null,
    })
    .select('*')
    .single();

  if (error) {
    console.error('sendGroupMessage error:', error);
    return { success: false, error: describeSendError(error) };
  }
  return { success: true, message: data as GroupChatMessage };
}

/**
 * Search users to add to a group chat by gamertag. Excludes soft-deleted users
 * and any ids already in `excludeIds` (e.g. current members). `users` is
 * publicly readable, so this runs on the anon client like the rest of lib.
 */
export async function searchUsersToAdd(
  query: string,
  excludeIds: string[] = [],
  limit = 20
): Promise<GroupChatMemberProfile[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const { data, error } = await supabase
    .from(TABLES.USERS)
    .select('id, gamertag, profile_image_url')
    .is('deleted_at', null)
    .ilike('gamertag', `%${trimmed}%`)
    .limit(limit + excludeIds.length);

  if (error) {
    console.error('searchUsersToAdd error:', error);
    return [];
  }

  const exclude = new Set(excludeIds);
  return (data ?? [])
    .filter((row) => !exclude.has(row.id as string))
    .slice(0, limit)
    .map((row) => ({
      user_id: row.id as string,
      gamertag: (row.gamertag as string) ?? 'user',
      profile_image_url: (row.profile_image_url as string) ?? undefined,
    }));
}

/**
 * Add members to a group chat and post a "joined" system message for each.
 * Gated by RLS to Gamerplug team members who are themselves in the chat
 * (policy "team members add members"); the UI mirrors that gate. `actorId` is
 * the sender of the system messages so they pass the message INSERT policy
 * (sender_id = auth.uid()) and read naturally as "<actor> added <gamertag>".
 */
export async function addGroupChatMembers(params: {
  groupChatId: string;
  actorId: string;
  users: GroupChatMemberProfile[];
}): Promise<{ success: boolean; added: GroupChatMemberProfile[]; error?: string }> {
  const { groupChatId, actorId, users } = params;
  if (!users.length) return { success: true, added: [] };

  const { error: insertError } = await supabase
    .from(TABLES.GROUP_CHAT_MEMBERS)
    .upsert(
      users.map((u) => ({ group_chat_id: groupChatId, user_id: u.user_id })),
      { onConflict: 'group_chat_id,user_id', ignoreDuplicates: true }
    );

  if (insertError) {
    console.error('addGroupChatMembers error:', insertError);
    return { success: false, added: [], error: insertError.message };
  }

  // System notices — best-effort; a failure here shouldn't undo the add.
  const { error: msgError } = await supabase.from(TABLES.GROUP_CHAT_MESSAGES).insert(
    users.map((u) => ({
      group_chat_id: groupChatId,
      sender_id: actorId,
      content: `added ${u.gamertag}`,
      message_type: 'join' as const,
    }))
  );
  if (msgError) console.error('addGroupChatMembers system message error:', msgError);

  return { success: true, added: users };
}

/**
 * Leave a group chat: post a "left" system notice, then remove your own
 * membership row. The notice is inserted first, while you're still a member, so
 * it passes the message INSERT policy. Self-delete is allowed by the
 * "leave own membership" policy.
 */
export async function leaveGroupChat(params: {
  groupChatId: string;
  userId: string;
}): Promise<{ success: boolean; error?: string }> {
  const { groupChatId, userId } = params;

  const { error: msgError } = await supabase.from(TABLES.GROUP_CHAT_MESSAGES).insert({
    group_chat_id: groupChatId,
    sender_id: userId,
    content: 'left the group',
    message_type: 'leave' as const,
  });
  if (msgError) console.error('leaveGroupChat system message error:', msgError);

  const { error } = await supabase
    .from(TABLES.GROUP_CHAT_MEMBERS)
    .delete()
    .eq('group_chat_id', groupChatId)
    .eq('user_id', userId);

  if (error) {
    console.error('leaveGroupChat error:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}

export async function markGroupChatRead(groupChatId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.GROUP_CHAT_MEMBERS)
    .update({ last_read_at: new Date().toISOString() })
    .eq('group_chat_id', groupChatId)
    .eq('user_id', userId);
  if (error) console.error('markGroupChatRead error:', error);
}

export function subscribeToGroupChatMessages(
  groupChatId: string,
  handlers: {
    onInsert: (message: GroupChatMessage) => void;
    onUpdate?: (message: GroupChatMessage) => void;
  } | ((message: GroupChatMessage) => void)
): () => void {
  const { onInsert, onUpdate } =
    typeof handlers === 'function' ? { onInsert: handlers, onUpdate: undefined } : handlers;

  const channel = supabase
    .channel(`group_chat_messages:${groupChatId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MESSAGES,
        filter: `group_chat_id=eq.${groupChatId}`,
      },
      (payload) => onInsert(payload.new as GroupChatMessage)
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MESSAGES,
        filter: `group_chat_id=eq.${groupChatId}`,
      },
      (payload) => onUpdate?.(payload.new as GroupChatMessage)
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// Live read receipts: fires when any member's last_read_at changes (i.e. they
// opened the chat), so the sender's "Delivered → Read" status updates in place.
export function subscribeToGroupChatReads(
  groupChatId: string,
  onRead: (row: { user_id: string; last_read_at: string | null }) => void
): () => void {
  const channel = supabase
    .channel(`group_chat_reads:${groupChatId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MEMBERS,
        filter: `group_chat_id=eq.${groupChatId}`,
      },
      (payload) => {
        const row = payload.new as { user_id: string; last_read_at: string | null };
        if (row?.user_id) onRead({ user_id: row.user_id, last_read_at: row.last_read_at ?? null });
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export async function pinGroupMessage(
  messageId: string,
  userId: string
): Promise<{ success: boolean; message?: GroupChatMessage; error?: string }> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .update({ pinned_at: new Date().toISOString(), pinned_by: userId })
    .eq('id', messageId)
    .select('*')
    .single();

  if (error) {
    console.error('pinGroupMessage error:', error);
    return { success: false, error: error.message };
  }
  return { success: true, message: data as GroupChatMessage };
}

export async function unpinGroupMessage(
  messageId: string
): Promise<{ success: boolean; message?: GroupChatMessage; error?: string }> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .update({ pinned_at: null, pinned_by: null })
    .eq('id', messageId)
    .select('*')
    .single();

  if (error) {
    console.error('unpinGroupMessage error:', error);
    return { success: false, error: error.message };
  }
  return { success: true, message: data as GroupChatMessage };
}

/**
 * Soft-delete a group message the current user sent. RLS ("Senders can soft
 * delete own group messages") rejects the write for anyone else; the row stays
 * so every member renders a tombstone and the realtime UPDATE fans it out.
 * Also clears any pin so a deleted message drops out of the pinned bar.
 */
export async function deleteGroupMessage(
  messageId: string
): Promise<{ success: boolean; message?: GroupChatMessage; error?: string }> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .update({ deleted_at: new Date().toISOString(), pinned_at: null, pinned_by: null })
    .eq('id', messageId)
    .select('*')
    .single();

  if (error) {
    console.error('deleteGroupMessage error:', error);
    return { success: false, error: error.message };
  }
  return { success: true, message: data as GroupChatMessage };
}

export async function getPinnedGroupMessages(
  groupChatId: string
): Promise<GroupChatMessage[]> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGES)
    .select('*')
    .eq('group_chat_id', groupChatId)
    .is('deleted_at', null)
    .not('pinned_at', 'is', null)
    .order('pinned_at', { ascending: false });

  if (error) {
    console.error('getPinnedGroupMessages error:', error);
    return [];
  }
  return (data ?? []) as GroupChatMessage[];
}

export async function getGroupChatReactions(
  messageIds: string[]
): Promise<GroupChatMessageReaction[]> {
  if (!messageIds.length) return [];
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGE_REACTIONS)
    .select('*')
    .in('message_id', messageIds);
  if (error) {
    console.error('getGroupChatReactions error:', error);
    return [];
  }
  return (data ?? []) as GroupChatMessageReaction[];
}

export async function setGroupMessageReaction(
  messageId: string,
  userId: string,
  emoji: string
): Promise<{ success: boolean; reaction?: GroupChatMessageReaction; error?: string }> {
  const { data, error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGE_REACTIONS)
    .upsert(
      { message_id: messageId, user_id: userId, emoji },
      { onConflict: 'message_id,user_id' }
    )
    .select('*')
    .single();
  if (error) {
    console.error('setGroupMessageReaction error:', error);
    return { success: false, error: error.message };
  }
  return { success: true, reaction: data as GroupChatMessageReaction };
}

export async function removeGroupMessageReaction(
  messageId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from(TABLES.GROUP_CHAT_MESSAGE_REACTIONS)
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId);
  if (error) {
    console.error('removeGroupMessageReaction error:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}

// Realtime reactions feed. Payloads for INSERT/UPDATE carry the new row; DELETE
// carries only the primary key, so callers must look up the reaction locally.
export function subscribeToGroupChatReactions(
  groupChatId: string,
  handlers: {
    onUpsert: (reaction: GroupChatMessageReaction) => void;
    onDelete: (reactionId: string) => void;
  }
): () => void {
  const channel = supabase
    .channel(`group_chat_message_reactions:${groupChatId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MESSAGE_REACTIONS,
      },
      (payload) => handlers.onUpsert(payload.new as GroupChatMessageReaction)
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MESSAGE_REACTIONS,
      },
      (payload) => handlers.onUpsert(payload.new as GroupChatMessageReaction)
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MESSAGE_REACTIONS,
      },
      (payload) => {
        const oldRow = payload.old as Partial<GroupChatMessageReaction>;
        if (oldRow?.id) handlers.onDelete(oldRow.id);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function subscribeToGroupInboxUpdates(
  onMessage: (message: GroupChatMessage) => void,
  onChatUpdate: (chat: GroupChat) => void,
  onRead?: (groupChatId: string) => void,
  userId?: string
): () => void {
  const channel = supabase
    .channel('group_chats_inbox')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: TABLES.GROUP_CHAT_MESSAGES },
      (payload) => onMessage(payload.new as GroupChatMessage)
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: TABLES.GROUP_CHATS },
      (payload) => onChatUpdate(payload.new as GroupChat)
    );

  // A read receipt (last_read_at) for this user — e.g. the thread was opened
  // on the phone or in another tab — should clear the inbox dot here too.
  if (onRead && userId) {
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: TABLES.GROUP_CHAT_MEMBERS,
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const row = payload.new as { group_chat_id?: string };
        if (row?.group_chat_id) onRead(row.group_chat_id);
      }
    );
  }

  channel.subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
